import { cva, type VariantProps } from 'class-variance-authority';
import { focusRing, uiMarker, uiMarkerLayout } from './shared';

/**
 * MEDIA — thumbnails and image frames.
 *
 * Two shapes, and the product drew both by hand. A *thumbnail* is a small
 * fixed-size picture in a row: the lot photo beside its title, four times as
 * `h-10 w-14 rounded-md border object-cover`, each with its own ImageOff
 * fallback beside it. A *media frame* is a picture that fills the width it is
 * given at a fixed aspect ratio, with something laid over it: a status badge
 * on a lot card, a check on a selected photo.
 */

const THUMB_SIZES = {
  landscape: { sm: 'h-10 w-14', md: 'h-14 w-20', lg: 'h-20 w-28' },
  square: { sm: 'size-10', md: 'size-14', lg: 'size-20' },
} as const;

export const thumbnailRecipe = cva(
  [
    uiMarker,
    'shrink-0 overflow-hidden rounded-md border border-border bg-muted',
  ],
  {
    variants: {
      size: { sm: '', md: '', lg: '' },
      shape: { landscape: '', square: '' },
    },
    compoundVariants: [
      { shape: 'landscape', size: 'sm', class: THUMB_SIZES.landscape.sm },
      { shape: 'landscape', size: 'md', class: THUMB_SIZES.landscape.md },
      { shape: 'landscape', size: 'lg', class: THUMB_SIZES.landscape.lg },
      { shape: 'square', size: 'sm', class: THUMB_SIZES.square.sm },
      { shape: 'square', size: 'md', class: THUMB_SIZES.square.md },
      { shape: 'square', size: 'lg', class: THUMB_SIZES.square.lg },
    ],
    defaultVariants: { size: 'sm', shape: 'landscape' },
  }
);

/**
 * The picture inside either container. Covers, never letterboxes — except in
 * a free-ratio frame, where the picture is shown whole at its own height:
 * the photo carousel's preview, the 404 page's artwork.
 */
export const mediaImageRecipe = cva([uiMarker, 'w-full'], {
  variants: {
    fit: {
      cover: 'h-full object-cover',
      contain: 'object-contain',
    },
    /** In a fixed-ratio frame the picture fills it; in a free one it sets the height. */
    ratio: {
      fixed: 'h-full',
      auto: 'h-auto',
    },
  },
  defaultVariants: { fit: 'cover', ratio: 'fixed' },
});
export type MediaImageRecipeProps = VariantProps<typeof mediaImageRecipe>;

/** What stands in for a picture there is not: a muted box with a glyph in it. */
export const mediaFallbackRecipe = cva([
  uiMarker,
  'flex h-full w-full items-center justify-center text-muted-foreground',
]);

/**
 * Aspect ratios written out, for the same reason Grid writes its columns out:
 * `aspect-[${ratio}]` compiles and produces no CSS.
 */
export const mediaFrameRecipe = cva(
  [uiMarker, 'relative w-full overflow-hidden'],
  {
    variants: {
      ratio: {
        /** No ratio: the frame is as tall as the picture. Pair with `fit="contain"`. */
        auto: '',
        '4/3': 'aspect-[4/3]',
        '3/2': 'aspect-[3/2]',
        '16/9': 'aspect-video',
        '16/11': 'aspect-[16/11]',
        '1/1': 'aspect-square',
      },
      radius: {
        none: 'rounded-none',
        /** For a frame at icon size — the map tile beside a place name. */
        sm: 'rounded-sm',
        md: 'rounded-md',
        lg: 'rounded-lg',
        xl: 'rounded-xl',
      },
      bordered: { true: 'border border-border', false: '' },
      /**
       * What shows behind the picture while it loads and beside it when it
       * does not fill the frame. `none` for artwork that must sit on the
       * page itself — a faded illustration, a preview whose parent already
       * draws the ground.
       */
      tone: {
        muted: 'bg-muted',
        none: '',
      },
    },
    defaultVariants: {
      ratio: '4/3',
      radius: 'none',
      bordered: false,
      tone: 'muted',
    },
  }
);

/**
 * A picture that opens something when pressed: a frame wrapped in a button.
 * `group` so the frame's picture can zoom a touch on hover. The radius
 * matches the frame's `lg`, so the focus ring follows its corners.
 */
export const mediaTileButtonRecipe = cva([
  uiMarker,
  'group block w-full cursor-pointer rounded-lg text-left',
  focusRing,
]);

/** Where the overlay goes. Top-right is where every badge in the app sits. */
export const mediaCornerRecipe = cva([
  uiMarker,
  'absolute right-2 top-2 flex items-center gap-1',
]);

export type ThumbnailRecipeProps = VariantProps<typeof thumbnailRecipe>;
export type MediaFrameRecipeProps = VariantProps<typeof mediaFrameRecipe>;

/**
 * PHOTOS ON A CALL — the strip along the call detail.
 *
 * The strip scrolls sideways and snaps to a photo, so a thumb flicks through
 * them; at the large size it bleeds to the screen's edges so the next photo
 * peeks in and says there are more. The small strip sits inside a card, a
 * checklist question's, so it keeps to the card's padding instead.
 */
export const photoStripRecipe = cva(
  [uiMarker, uiMarkerLayout, 'flex snap-x snap-mandatory overflow-x-auto pb-1'],
  {
    variants: {
      /** The Figma's call photos sit 4 apart; an answer's thumbnails 8. */
      size: {
        lg: '-mx-4 gap-1 px-4',
        sm: 'gap-2',
      },
    },
    defaultVariants: { size: 'lg' },
  }
);

/**
 * A large portrait card, as the Figma's photos-added frame draws a call's
 * photos, so a driver can see what was shot without opening it.
 */
export const photoTileRecipe = cva(
  [
    uiMarker,
    'relative shrink-0 snap-start overflow-hidden bg-muted',
    focusRing,
  ],
  {
    variants: {
      /**
       * `lg` is the Figma's call photo: 240 high and as wide as the picture
       * is at that height, up to most of the strip so the next one peeks
       * in. It was a fixed 208 square crop, which cut a landscape photo of
       * a car down to its middle. `sm` is a thumbnail, for photos that are
       * evidence on one answer rather than the subject of the screen: a
       * failed checklist question's.
       */
      size: {
        lg: 'h-60 w-auto min-w-24 max-w-[85%] rounded-xl',
        sm: 'size-20 rounded-lg',
      },
      /** The tile that opens the camera or library rather than a photo. */
      add: {
        true: 'flex flex-col items-center justify-center gap-1 border border-dashed border-foreground/10 bg-background text-muted-foreground hover:bg-foreground/5',
        false: '',
      },
    },
    defaultVariants: { add: false, size: 'lg' },
  }
);

/**
 * THE PHOTOS SHEET — every photo on a call, one under another at the
 * sheet's width, as the Figma's photos-added sheet lists them, each with a
 * round comment button in its corner.
 */
export const photoListRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex flex-col gap-4',
]);

export const photoListItemRecipe = cva(
  [
    uiMarker,
    // Vertical drags still scroll the sheet; two fingers are the pinch's.
    'relative touch-pan-y',
    'transition-[transform,filter,opacity] duration-base ease-out',
  ],
  {
    variants: {
      /**
       * A pinch lifts its photo over the rest and blurs them, as the
       * Figma's Pinch Zoom frames draw it; letting go settles all of them
       * back. `zooming` drops the transition so the photo follows the
       * fingers rather than trailing them.
       */
      pinch: {
        rest: '',
        zooming: 'z-10 transition-none',
        behind: 'opacity-60 blur-md',
      },
    },
    defaultVariants: { pinch: 'rest' },
  }
);

export const photoListPhotoRecipe = cva([
  uiMarker,
  'block w-full overflow-hidden rounded-xl bg-muted',
  focusRing,
]);

/** The comment button in a listed photo's bottom-right corner. */
export const photoListCornerRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'absolute bottom-3 right-3',
]);

/**
 * ONE PHOTO AT A TIME — the photo sheet's picture, swiped sideways to the
 * next. A row of full-width slides that snaps, so the browser does the
 * swipe and the momentum; the sheet only hears which photo landed.
 */
export const photoPagerRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain',
  '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
]);

export const photoPagerSlideRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'w-full shrink-0 snap-center',
]);

/** Over a photo that is still sending, or failed to: dims it, says which. */
/**
 * A call photo at the strip's height and its own width: the picture sets
 * the tile's width rather than being cropped to one, as MediaFrame's
 * width-led ratios would.
 */
export const photoTileImageRecipe = cva([
  uiMarker,
  'block h-full w-auto max-w-none object-cover',
]);

/**
 * How many photos the call has, on the first one's top-left corner, as the
 * Figma's call detail draws it: a dark, blurred pill with white words.
 */
export const photoTileCountRecipe = cva([
  uiMarker,
  'pointer-events-none absolute left-2 top-2 rounded-pill bg-foreground/50 px-2 py-1 text-background backdrop-blur-sm',
]);

export const photoTileOverlayRecipe = cva([
  uiMarker,
  'absolute inset-0 flex items-center justify-center bg-foreground/50 text-background',
]);
